<template>
  <!-- Cover Image Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="true"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Cover Image</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <div v-if="coverImageUrl" class="image-preview-container">
          <img :src="coverImageUrl" alt="Cover Preview" class="image-preview" />
          <button class="remove-image-button" @click="removeCoverImage">
            <v-icon>mdi-delete</v-icon>
            Remove Image
          </button>
        </div>
        <div v-else class="empty-image-message">
          No cover image selected. Please upload an image for your lesson plan.
        </div>
        <label class="upload-button">
          {{ coverImageUrl ? "Change Image" : "Upload Image" }}
          <input
            ref="fileInputRef"
            type="file"
            @change="handleCoverImageUpload"
            accept="image/*"
            style="display: none"
          />
        </label>
      </div>
    </template>
  </AccordionItem>

  <!-- Locale Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Locale</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <div v-if="isLoadingLocales" class="loading-materials">
          <p>Loading locales...</p>
        </div>
        <MultipleSelect
          v-else
          v-model="locale"
          @update:modelValue="updateLocale"
          placeholder="Select locale"
          :options="localeOptions"
          variant="form"
          :teleport="true"
          :match-trigger-width="true"
        />
      </div>
    </template>
  </AccordionItem>

  <!-- Makercart Activity Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Makercart Activity</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <div v-if="isLoadingMakercartActivities" class="loading-activities">
          <p>Loading activities...</p>
        </div>
        <SingleSelect
          v-else
          v-model="makercartActivity"
          @update:modelValue="updateMakercartActivity"
          placeholder="Select lesson materials"
          :options="makercartActivityOptions"
          variant="form"
          :teleport="true"
          :match-trigger-width="true"
          :show-search="true"
          input-placeholder="Search lesson materials..."
        />
      </div>
    </template>
  </AccordionItem>

  <!-- Materials Provided Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Materials Provided</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <div v-if="isLoadingMaterials" class="loading-materials">
          <p>Loading materials...</p>
        </div>
        <div
          v-else-if="materialsProvidedList.length > 0"
          class="materials-display"
        >
          <div class="materials-grid">
            <div
              v-for="(material, index) in materialsProvidedList"
              :key="index"
              class="material-item"
            >
              <span class="material-name">{{ material }}</span>
            </div>
          </div>
        </div>
        <div v-else class="empty-materials-message">
          {{
            makercartActivity
              ? "No materials provided for this activity"
              : "Select a makercart activity to view materials"
          }}
        </div>
      </div>
    </template>
  </AccordionItem>

  <!-- You'll Need to Provide Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">You'll Need to Provide</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <div v-if="isLoadingMaterials" class="loading-materials">
          <p>Loading materials...</p>
        </div>
        <div
          v-else-if="youllNeedToProvideList.length > 0"
          class="materials-display"
        >
          <div class="materials-grid">
            <div
              v-for="(material, index) in youllNeedToProvideList"
              :key="index"
              class="material-item"
            >
              <span class="material-name">{{ material }}</span>
            </div>
          </div>
        </div>
        <div v-else class="empty-materials-message">
          {{
            makercartActivity
              ? "No additional materials needed for this activity"
              : "Select a makercart activity to view additional materials"
          }}
        </div>
      </div>
    </template>
  </AccordionItem>

  <!-- Subject Integration Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Subject Integration</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <div v-if="isLoadingMaterials" class="loading-materials">
          <p>Loading subject integration...</p>
        </div>
        <div
          v-else-if="subjectIntegrationDisplay"
          class="subject-integration-display"
        >
          <div class="subject-integration-item">
            <span class="subject-integration-name">{{
              subjectIntegrationDisplay
            }}</span>
          </div>
        </div>
        <div v-else class="empty-materials-message">
          {{
            makercartActivity
              ? "No subject integration available for this activity"
              : "Select a makercart activity to view subject integration"
          }}
        </div>
      </div>
    </template>
  </AccordionItem>

  <!-- Hardware Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Hardware</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <div v-if="isLoadingMaterials" class="loading-materials">
          <p>Loading hardware...</p>
        </div>
        <div v-else-if="hardwareList.length > 0" class="hardware-display">
          <div class="hardware-grid">
            <div
              v-for="(hardware, index) in hardwareList"
              :key="index"
              class="hardware-item"
            >
              <span class="hardware-name">{{ hardware }}</span>
            </div>
          </div>
        </div>
        <div v-else class="empty-materials-message">
          {{
            makercartActivity
              ? "No hardware available for this activity"
              : "Select a makercart activity to view hardware"
          }}
        </div>
      </div>
    </template>
  </AccordionItem>

  <!-- Maker Approach Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Maker Approach</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <div v-if="isLoadingMaterials" class="loading-materials">
          <p>Loading maker approach...</p>
        </div>
        <div v-else-if="makerApproach" class="maker-approach-display">
          <div class="maker-approach-item">
            <span class="maker-approach-name">{{ makerApproach }}</span>
          </div>
        </div>
        <div v-else class="empty-materials-message">
          {{
            makercartActivity
              ? "No maker approach available for this activity"
              : "Select a makercart activity to view maker approach"
          }}
        </div>
      </div>
    </template>
  </AccordionItem>

  <!-- Focus Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Focus</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <SingleSelect
          v-model="focus"
          @update:modelValue="updateFocus"
          placeholder="Select focus option"
          :options="focusOptions"
          variant="form"
          :teleport="true"
          :match-trigger-width="true"
        />
      </div>
    </template>
  </AccordionItem>

  <!-- Grade Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Grade</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <SingleSelect
          v-model="grade"
          @update:modelValue="updateGrade"
          placeholder="Select grade option"
          :options="gradeOptions"
          variant="form"
          :teleport="true"
          :match-trigger-width="true"
        />
      </div>
    </template>
  </AccordionItem>

  <!-- Visibility Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Visibility</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <SingleSelect
          v-model="visibility"
          @update:modelValue="updateVisibility"
          placeholder="Select visibility option"
          :options="visibilityOptions"
          variant="form"
          :teleport="true"
          :match-trigger-width="true"
        />
      </div>
    </template>
  </AccordionItem>

  <!-- School Section -->
  <AccordionItem
    class="right-sidebar-item"
    :class="{ 'disabled-section': visibility === 'public_preview' }"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
  >
    <template v-slot:title>
      <div
        class="right-sidebar-title"
        :class="{ 'disabled-title': visibility === 'public_preview' }"
      >
        School
      </div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <div
          v-if="visibility === 'public_preview'"
          class="disabled-school-message"
        >
          <p>
            School selection is disabled when visibility is set to "Public
            Preview". Change visibility to "Restricted View" to enable school
            selection.
          </p>
        </div>

        <template v-else>
          <!-- Selected schools display -->
          <div class="tags-container" v-if="selectedSchoolItems.length > 0">
            <div
              v-for="(tag, index) in selectedSchoolItems"
              :key="index"
              class="tag"
            >
              <p>{{ getSchoolDisplayText(tag) }}</p>
              <button @click="removeSchoolTag(index)" class="delete-tag-button">
                <v-icon>mdi-close</v-icon>
              </button>
            </div>
          </div>
          <div v-else class="empty-tags-message">No schools selected</div>

          <SchoolSelectionInput
            v-model="selectedSchoolObjects"
            :multiple="true"
            placeholder="Select school options"
            :searchFunction="fetchSchoolsForInput"
            label=""
          />
        </template>
      </div>
    </template>
  </AccordionItem>

  <!-- Loading State for Categories -->
  <div v-if="isLoadingCategories" class="loading-container">
    <p>Loading categories...</p>
  </div>

  <!-- Empty State for Categories -->
  <div v-else-if="categorySections.length === 0" class="empty-state">
    <p>No categories available.</p>
  </div>

  <!-- Dynamic Category Sections -->
  <template v-else>
    <!-- Other Categories Section -->
    <template v-if="hasNonStandardsCategories">
      <!-- Non-Standards Categories -->
      <AccordionItem
        v-for="section in nonStandardsCategories"
        :key="section.id"
        class="right-sidebar-item"
        :icon-position="`left`"
        :collapsedIcon="`/images/arrow-bottom.svg`"
        :expandedIcon="`/images/arrow-bottom.svg`"
        @toggle="(isOpen) => handleCategoryAccordionToggle(isOpen, section.id)"
      >
        <template v-slot:title>
          <div class="right-sidebar-title">{{ section.title }}</div>
        </template>
        <template v-slot:content>
          <div class="content-container">
            <!-- Selected tags display -->
            <div
              class="tags-container"
              v-if="section.selectedItems && section.selectedItems.length > 0"
            >
              <div
                v-for="(tag, index) in section.selectedItems"
                :key="index"
                class="tag"
              >
                <p>{{ getCategoryTagDisplayText(tag, section) }}</p>
                <button
                  @click="removeCategoryTag(section, index)"
                  class="delete-tag-button"
                >
                  <v-icon>mdi-close</v-icon>
                </button>
              </div>
            </div>
            <div v-else class="empty-tags-message">No tags selected</div>

            <div class="select-container">
              <MultipleSelect
                v-model="section.selectedValues"
                :placeholder="`Select ${section.title}...`"
                :options="section.formattedOptions"
                maxHeight="200px"
                variant="form"
                :teleport="true"
                :match-trigger-width="true"
                @update:modelValue="
                  handleCategoryMultipleSelectionChange(section)
                "
              />
            </div>

            <div
              v-if="section.isLoadingOptions"
              class="options-loading-indicator"
            >
              Loading options...
            </div>
          </div>
        </template>
      </AccordionItem>
    </template>

    <!-- Standards Section -->
    <AccordionItem
      v-if="hasStandardsCategories"
      class="right-sidebar-item standards-main-accordion"
      :icon-position="`left`"
      :collapsedIcon="`/images/arrow-bottom.svg`"
      :expandedIcon="`/images/arrow-bottom.svg`"
      :initiallyOpen="false"
    >
      <template v-slot:title>
        <div class="right-sidebar-title standards-main-title">Standards</div>
      </template>
      <template v-slot:content>
        <div class="standards-accordion-content">
          <!-- Standards Categories -->
          <AccordionItem
            v-for="section in standardsCategories"
            :key="section.id"
            class="right-sidebar-item standards-category"
            :icon-position="`left`"
            :collapsedIcon="`/images/arrow-bottom.svg`"
            :expandedIcon="`/images/arrow-bottom.svg`"
            @toggle="
              (isOpen) => handleCategoryAccordionToggle(isOpen, section.id)
            "
          >
            <template v-slot:title>
              <div class="right-sidebar-title">{{ section.title }}</div>
            </template>
            <template v-slot:content>
              <div class="content-container">
                <!-- Selected tags display -->
                <div
                  class="tags-container"
                  v-if="
                    section.selectedItems && section.selectedItems.length > 0
                  "
                >
                  <div
                    v-for="(tag, index) in section.selectedItems"
                    :key="index"
                    class="tag"
                  >
                    <p>{{ getCategoryTagDisplayText(tag, section) }}</p>
                    <button
                      @click="removeCategoryTag(section, index)"
                      class="delete-tag-button"
                    >
                      <v-icon>mdi-close</v-icon>
                    </button>
                  </div>
                </div>
                <div v-else class="empty-tags-message">No tags selected</div>

                <div class="select-container">
                  <MultipleSelect
                    v-model="section.selectedValues"
                    :placeholder="`Select ${section.title}...`"
                    :options="section.formattedOptions"
                    maxHeight="200px"
                    variant="form"
                    :teleport="true"
                    :match-trigger-width="true"
                    :show-search="true"
                    order="a-z"
                    :input-placeholder="`Search ${section.title}...`"
                    @update:modelValue="
                      handleCategoryMultipleSelectionChange(section)
                    "
                  />
                </div>

                <div
                  v-if="section.isLoadingOptions"
                  class="options-loading-indicator"
                >
                  Loading options...
                </div>
              </div>
            </template>
          </AccordionItem>
        </div>
      </template>
    </AccordionItem>
  </template>

  <!-- Permalink Section -->
  <AccordionItem
    class="right-sidebar-item"
    :icon-position="`left`"
    :collapsedIcon="`/images/arrow-bottom.svg`"
    :expandedIcon="`/images/arrow-bottom.svg`"
    :initiallyOpen="false"
  >
    <template v-slot:title>
      <div class="right-sidebar-title">Permalink</div>
    </template>
    <template v-slot:content>
      <div class="content-container">
        <div class="permalink-container">
          <BaseInput
            type="text"
            class="permalink-input"
            v-model="permalink"
            @input="updatePermalink"
            placeholder="Enter permalink"
          />
        </div>
      </div>
    </template>
  </AccordionItem>
</template>

<script setup>
import BaseInput from "@/components/BaseInput.vue";
import SingleSelect from "@/components/common/SingleSelect.vue";
import MultipleSelect from "@/components/common/MultipleSelect.vue";
import SchoolSelectionInput from "@/components/common/SchoolSelectionInput.vue";
import categoryAdminService from "@/services/categoryAdminService";
import makercartActivityService from "@/services/makercartActivityService";
import stemStudioService from "@/services/stemStudioService";
import userWalkthroughService from "@/services/userWalkthroughService";
import { CATEGORY_TYPES } from "@/utils/constants";
import AccordionItem from "@/views/ChinaUs/AccordionItem.vue";
import {
  defineEmits,
  defineExpose,
  onMounted,
  reactive,
  ref,
  watch,
  computed,
} from "vue";

const emit = defineEmits([
  "cover-image-updated",
  "tag-sections-updated",
  "category-sections-updated",
  "permalink-updated",
  "makercart-activity-updated",
  "materials-provided-updated",
  "youll-need-to-provide-updated",
  "subject-integration-updated",
  "subject-updated",
  "maker-approach-updated",
  "hardware-updated",
  "visibility-updated",
  "focus-updated",
  "grade-updated",
  "airtable-id-updated",
  "school-updated",
  "locale-updated",
]);

const coverImageUrl = ref(null);
const fileInputRef = ref(null); // Reference to the file input element
const isLoading = ref(true);
const permalink = ref(""); // Add permalink reference
const loadedOptions = ref(new Set()); // Track which categories have loaded options

// New form fields
const makercartActivity = ref("");
const materialsProvided = ref("");
const youllNeedToProvide = ref("");
const subjectIntegration = ref("");
const makerApproach = ref("");
const visibility = ref("");
const focus = ref("");
const grade = ref("");
const school = ref([]);
const selectedSchoolObjects = ref([]); // Store selected school objects for SchoolSelectionInput
const selectedSchoolItems = ref([]); // Store selected school names for display
const schoolCategoryId = ref(null);

// Locales
const locale = ref([]);
const localeOptions = ref([]);
const isLoadingLocales = ref(false);

// Makercart Activity options and materials data
const makercartActivityOptions = ref([]);
const isLoadingMakercartActivities = ref(false);
const materialsProvidedList = ref([]);
const youllNeedToProvideList = ref([]);
const isLoadingMaterials = ref(false);
const subjectIntegrationDisplay = ref("");
const hardwareList = ref([]);
const schoolOptions = ref([]);

// Cache for all makercart activities data
const makercartActivitiesCache = ref(new Map());

// Visibility options
const visibilityOptions = ref([
  { text: "Public Preview", value: "public_preview" },
  { text: "Restricted View", value: "restricted_view" },
]);

// Focus options
const focusOptions = ref([
  { text: "Sustainability", value: "Sustainability" },
  { text: "Engineering Design", value: "Engineering Design" },
  { text: "Classic Maker", value: "Classic Maker" },
]);

// Grade options
const gradeOptions = ref([]);

// Computed properties for organizing categories
const standardsCategories = computed(() => {
  return categorySections.filter((section) => isStandardCategory(section));
});

const nonStandardsCategories = computed(() => {
  return categorySections.filter((section) => !isStandardCategory(section));
});

const hasStandardsCategories = computed(() => {
  return standardsCategories.value.length > 0;
});

const hasNonStandardsCategories = computed(() => {
  return nonStandardsCategories.value.length > 0;
});

// Helper method to identify if a category is a standard
const isStandardCategory = (section) => {
  if (!section || !section.name) return false;
  return section.name.toLowerCase().includes("standard");
};

const handleCoverImageUpload = (event) => {
  const file = event.target.files[0];
  if (file && file.type.startsWith("image/")) {
    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image file size should be less than 5MB.");
      event.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      coverImageUrl.value = e.target.result;
      emit("cover-image-updated", coverImageUrl.value);
    };
    reader.readAsDataURL(file);
  } else if (file) {
    // Only show error if a file was actually selected
    // Handle invalid file type
    alert("Please select an image file.");
    // Reset file input
    event.target.value = null;
  }
};

// Add function to remove the cover image
const removeCoverImage = () => {
  coverImageUrl.value = null;
  emit("cover-image-updated", null);

  // Reset the file input element to ensure it can trigger onChange for the same file
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
};

// Define the tag sections with initial empty state
const tagSections = reactive([]);

// Define the category sections with initial empty state
const categorySections = reactive([]);
const isLoadingCategories = ref(false);
const loadedCategoryOptions = ref(new Set()); // Track which categories have loaded options

// Fetch general categories (excluding school) from API
const fetchCategories = async () => {
  try {
    isLoadingCategories.value = true;
    const response = await categoryAdminService.getCategoriesLesson();
    console.log("response", response);

    if (response && response.data.data && response.data.data.rows) {
      // Clear existing category sections
      categorySections.splice(0, categorySections.length);

      // Create category sections from categories (excluding school)
      for (const category of response.data.data.rows) {
        const isMakercartActivitiesCategory =
          category.category_for?.some(
            (item) => item === CATEGORY_TYPES.LessonMaterials
          ) || false;

        // Skip if not for MakercartActivities or if it's a school category
        if (!isMakercartActivitiesCategory || category.name === "school")
          continue;

        const section = {
          id: category.id,
          name: category.name, // Add name property for standard identification
          title: category.title,
          options: [],
          formattedOptions: [], // Options formatted for BaseInput
          selectedItems: [], // Text representation of selected items
          selectedValues: [], // Values for v-model binding
          isLoadingOptions: false,
        };

        categorySections.push(section);
      }
    }
  } catch (error) {
    console.error("Error fetching categories:", error);
  } finally {
    isLoadingCategories.value = false;
  }
};

// Fetch school-specific categories from API for the existing school section
const fetchSchoolCategories = async () => {
  try {
    isLoading.value = true;
    const response = await categoryAdminService.getCategoriesLesson({
      search: "school",
    });
    console.log("school response", response);

    if (response && response.data.data && response.data.data.rows) {
      // Clear existing tag sections
      tagSections.splice(0, tagSections.length);

      // Create tag sections from categories
      for (const category of response.data.data.rows) {
        const isLessonPlanCategory =
          category.category_for?.some(
            (item) => item === CATEGORY_TYPES.LessonMaterials
          ) || false;

        if (!isLessonPlanCategory) continue;

        if (category.name === "school") {
          schoolCategoryId.value = category.id;
        }

        const section = {
          id: category.id,
          title: category.title,
          options: [],
          formattedOptions: [], // Options formatted for BaseInput
          selectedItems: [], // Text representation of selected items
          selectedValues: [], // Values for v-model binding
          selectedValue: "", // Single value for Grade section
          isLoadingOptions: false,
        };

        tagSections.push(section);
      }

      // Fetch initial school options
      await fetchSchoolOptions();
    }
  } catch (error) {
    console.error("Error fetching school categories:", error);
  } finally {
    isLoading.value = false;
  }
};

// Fetch school options specifically (Initial Data)
const fetchSchoolOptions = async () => {
  try {
    if (!schoolCategoryId.value) {
      const response = await categoryAdminService.getCategoriesLesson({
        search: "school",
      });

      if (response && response.data.data && response.data.data.rows) {
        const schoolCategory = response.data.data.rows.find(
          (category) => category.name === "school"
        );
        if (schoolCategory) {
          schoolCategoryId.value = schoolCategory.id;
        }
      }
    }

    if (schoolCategoryId.value) {
      const optionsResponse = await categoryAdminService.getById(
        schoolCategoryId.value
      );

      if (
        optionsResponse &&
        optionsResponse.data.data &&
        optionsResponse.data.data.terms
      ) {
        schoolOptions.value = optionsResponse.data.data.terms.map((term) => ({
          text: term.title,
          value: term.title, // Use title as value
        }));
      }
    }
  } catch (error) {
    console.error("Error fetching school options:", error);
  }
};

// API Search function for schools - returns objects for SchoolSelectionInput
const fetchSchoolsForInput = async (query) => {
  try {
    if (!query || query.trim() === "") {
      // If empty query, we might want to return default options if we have them,
      // but SchoolSelectionInput handles default fetching.
      // However, we can return the category-based options if available.
      await fetchSchoolOptions();

      // Convert options { text, value } to objects { id, name }
      return schoolOptions.value.map((opt) => ({
        id: null,
        name: opt.text,
        location: "",
        isNew: false,
      }));
    }

    const response = await userWalkthroughService.getSchools(query.trim());
    if (response?.data?.success && Array.isArray(response.data.data)) {
      return response.data.data.map((school, index) => ({
        id: index + 1, // Or proper ID
        name: school.title,
        location: school.country,
        originalData: school,
      }));
    }
    return [];
  } catch (error) {
    console.error("Error searching schools:", error);
    return [];
  }
};

// Format options for BaseInput component
const formatOptions = (options) => {
  return options.map((option) => ({
    text: option.title || option,
    value: JSON.stringify({
      id: option.id || option,
      title: option.title || option,
    }),
  }));
};

// Format options for category sections
const formatCategoryOptions = (options) => {
  return options.map((option) => ({
    text: option.title || option,
    value: JSON.stringify({
      id: option.id || option,
      title: option.title || option,
    }),
  }));
};

// Get display text for a category tag based on its value
const getCategoryTagDisplayText = (tagValue, section) => {
  try {
    const parsedValue = JSON.parse(tagValue);
    if (
      section &&
      isStandardCategory(section) &&
      parsedValue.standardCategory
    ) {
      return `${parsedValue.standardCategory}: ${parsedValue.title}`;
    }
    return parsedValue.title;
  } catch (e) {
    // Fallback for any unparseable values
    return tagValue;
  }
};

// Fetch options for a specific general category (not school)
const fetchGeneralCategoryOptions = async (categoryId) => {
  // Skip if options are already loaded or currently loading
  const sectionIndex = categorySections.findIndex(
    (section) => section.id === categoryId
  );
  if (
    sectionIndex === -1 ||
    loadedCategoryOptions.value.has(categoryId) ||
    categorySections[sectionIndex].isLoadingOptions
  ) {
    return;
  }

  try {
    // Set loading state for this specific category
    categorySections[sectionIndex].isLoadingOptions = true;

    const response = await categoryAdminService.getById(categoryId);

    if (response && response.data.data && response.data.data.terms) {
      // Update options with term objects containing id and title
      const terms = response.data.data.terms.map((term) => ({
        id: term.id,
        title: term.title,
      }));
      categorySections[sectionIndex].options = terms;

      // Format options for BaseInput
      categorySections[sectionIndex].formattedOptions =
        formatCategoryOptions(terms);

      // Mark this category as having loaded options
      loadedCategoryOptions.value.add(categoryId);
    }
  } catch (error) {
    console.error(`Error fetching options for category ${categoryId}:`, error);
  } finally {
    if (sectionIndex !== -1) {
      categorySections[sectionIndex].isLoadingOptions = false;
    }
  }
};

// Handle category accordion toggle to load options when opened
const handleCategoryAccordionToggle = (isOpen, categoryId) => {
  console.log("handleCategoryAccordionToggle", isOpen, categoryId);
  if (isOpen && !loadedCategoryOptions.value.has(categoryId)) {
    fetchGeneralCategoryOptions(categoryId);
  }
};

// Handle multiple selection change for categories
const handleCategoryMultipleSelectionChange = (section) => {
  console.log("handleCategoryMultipleSelectionChange", section.selectedValues);

  // Ensure selectedValues is an array before filtering
  if (!Array.isArray(section.selectedValues)) {
    section.selectedValues = [];
  }

  // Update selectedItems array for display
  section.selectedItems = section.selectedValues.filter(
    (value) => value && value.trim() !== ""
  );

  // Emit updated category sections
  emitUpdatedCategorySections();
};

// Remove a category tag
const removeCategoryTag = (section, index) => {
  // Ensure arrays exist before removing
  if (!Array.isArray(section.selectedItems)) {
    section.selectedItems = [];
  }
  if (!Array.isArray(section.selectedValues)) {
    section.selectedValues = [];
  }

  // Remove from selectedItems and selectedValues
  if (index >= 0 && index < section.selectedItems.length) {
    section.selectedItems.splice(index, 1);
  }
  if (index >= 0 && index < section.selectedValues.length) {
    section.selectedValues.splice(index, 1);
  }

  // Emit updated category sections
  emitUpdatedCategorySections();
};

// Emit updated category sections
const emitUpdatedCategorySections = () => {
  // Ensure all sections have proper array initialization before emitting
  categorySections.forEach((section) => {
    if (!Array.isArray(section.selectedItems)) {
      section.selectedItems = [];
    }
    if (!Array.isArray(section.selectedValues)) {
      section.selectedValues = [];
    }
  });

  // Format the category sections for emit
  const formattedCategorySections = categorySections.map((section) => ({
    id: section.id,
    title: section.title,
    selected: section.selectedItems || [],
  }));

  emit("category-sections-updated", formattedCategorySections);
};

// Fetch options for a specific category
const fetchCategoryOptions = async (categoryId) => {
  // Skip if options are already loaded or currently loading
  const sectionIndex = tagSections.findIndex(
    (section) => section.id === categoryId
  );
  if (
    sectionIndex === -1 ||
    loadedOptions.value.has(categoryId) ||
    tagSections[sectionIndex].isLoadingOptions
  ) {
    return;
  }

  try {
    // Set loading state for this specific category
    tagSections[sectionIndex].isLoadingOptions = true;

    const response = await categoryAdminService.getById(categoryId);

    if (response && response.data.data && response.data.data.terms) {
      // Update options with term objects containing id and title
      const terms = response.data.data.terms.map((term) => ({
        id: term.id,
        title: term.title,
      }));
      tagSections[sectionIndex].options = terms;

      // Format options for BaseInput
      tagSections[sectionIndex].formattedOptions = formatOptions(terms);

      // Mark this category as having loaded options
      loadedOptions.value.add(categoryId);
    }
  } catch (error) {
    console.error(`Error fetching options for category ${categoryId}:`, error);
  } finally {
    if (sectionIndex !== -1) {
      tagSections[sectionIndex].isLoadingOptions = false;
    }
  }
};

// Update permalink function
const updatePermalink = (event) => {
  permalink.value = event.target.value;
  emit("permalink-updated", permalink.value);
};

// Fetch makercart activities from API
const fetchMakercartActivities = async (localeParam = null) => {
  try {
    isLoadingMakercartActivities.value = true;
    const params = {};
    if (localeParam) {
      params.locale = localeParam;
    }

    const response =
      await makercartActivityService.getMakercartActivities(params);

    if (response && response.data && response.data.data) {
      // Cache all activity data for later use
      makercartActivitiesCache.value.clear();

      makercartActivityOptions.value = response.data.data.map((activity) => {
        // Store complete activity data in cache
        makercartActivitiesCache.value.set(activity.makercart_id, activity);

        return {
          text: activity.makercart_activity,
          value: activity.makercart_id,
        };
      });
    }
  } catch (error) {
    console.error("Error fetching makercart activities:", error);
  } finally {
    isLoadingMakercartActivities.value = false;
  }
};

// Get activity data from cache instead of making API call
const getActivityDataFromCache = (activityId) => {
  try {
    isLoadingMaterials.value = true;

    // Get activity from cache
    const activity = makercartActivitiesCache.value.get(activityId);

    if (!activity) {
      console.error(`Activity with ID ${activityId} not found in cache`);
      // Reset materials on error
      resetMaterialsData();
      return;
    }

    // Update subject integration display
    if (activity.subject && activity.subject !== "N/A") {
      subjectIntegrationDisplay.value = activity.subject;
      subjectIntegration.value = activity.subject;
      emit("subject-integration-updated", activity.subject);
      emit("subject-updated", activity.subject);
    } else {
      subjectIntegrationDisplay.value = "";
      subjectIntegration.value = "";
      emit("subject-integration-updated", "");
      emit("subject-updated", "");
    }

    // Update maker approach from cached data
    if (activity.maker_approach) {
      makerApproach.value = activity.maker_approach;
      emit("maker-approach-updated", activity.maker_approach);
    } else {
      makerApproach.value = "";
      emit("maker-approach-updated", "");
    }

    // Process materials if material_needed exists and has items
    if (activity.material_needed && activity.material_needed.length > 0) {
      // Get materials in bulk using the existing API call
      fetchMaterialsForCachedActivity(activity.material_needed);
    } else {
      // No materials for this activity
      resetMaterialsData();
    }

    // Update hardware from cached data
    if (!isHardwareEmpty(activity.hardware_associated)) {
      if (Array.isArray(activity.hardware_associated)) {
        hardwareList.value = [...activity.hardware_associated];
      } else if (typeof activity.hardware_associated === "string") {
        hardwareList.value = [activity.hardware_associated];
      }
      emit("hardware-updated", hardwareList.value);
    } else {
      resetHardwareData();
    }

    // Update grade from cached data
    if (activity.grade_level && activity.grade_level.length > 0) {
      gradeOptions.value = activity.grade_level.map((grade) => {
        return {
          text: grade,
          value: grade,
        };
      });
    } else {
      gradeOptions.value = [];
    }
  } catch (error) {
    console.error("Error getting activity data from cache:", error);
    resetMaterialsData();
    resetHardwareData();
  } finally {
    isLoadingMaterials.value = false;
  }
};

// Helper function to reset materials data
const resetMaterialsData = () => {
  materialsProvidedList.value = [];
  youllNeedToProvideList.value = [];
  materialsProvided.value = "";
  youllNeedToProvide.value = "";
  emit("materials-provided-updated", "");
  emit("youll-need-to-provide-updated", "");
};

// Helper function to validate and process hardware data
const isHardwareEmpty = (hardware) => {
  if (!hardware) return true;
  if (typeof hardware === "string" && hardware.trim() === "") return true;
  if (Array.isArray(hardware) && hardware.length === 0) return true;
  return false;
};

// Helper function to reset hardware data
const resetHardwareData = () => {
  hardwareList.value = [];
  emit("hardware-updated", []);
};

// Fetch materials for cached activity (only when materials are needed)
const fetchMaterialsForCachedActivity = async (materialIds) => {
  try {
    // Get materials in bulk
    const materialsResponse = await stemStudioService.getMaterialByIdBulk(
      materialIds.join(",")
    );

    if (
      materialsResponse &&
      materialsResponse.data &&
      materialsResponse.data.data
    ) {
      const materials = materialsResponse.data.data;

      // Separate materials based on includedInMC
      materialsProvidedList.value = materials
        .filter((material) => material.includedInMC === true)
        .map((material) => material.name);

      youllNeedToProvideList.value = materials
        .filter((material) => material.includedInMC === false)
        .map((material) => material.name);

      // Update the text fields
      materialsProvided.value = materialsProvidedList.value.join(", ");
      youllNeedToProvide.value = youllNeedToProvideList.value.join(", ");

      // Emit updates
      emit("materials-provided-updated", materialsProvided.value);
      emit("youll-need-to-provide-updated", youllNeedToProvide.value);
    }
  } catch (error) {
    console.error("Error fetching materials for cached activity:", error);
    resetMaterialsData();
    resetHardwareData();
  }
};

// Watch for makercart activity changes
watch(makercartActivity, (newValue) => {
  if (newValue) {
    // Check if cache is ready, if not wait for it
    if (
      makercartActivitiesCache.value.size === 0 &&
      !isLoadingMakercartActivities.value
    ) {
      // Cache is empty but not loading, wait for it to load
      const unwatch = watch(
        makercartActivitiesCache,
        (newCache) => {
          if (newCache.size > 0) {
            getActivityDataFromCache(newValue);
            unwatch();
          }
        },
        { immediate: true }
      );
    } else if (makercartActivitiesCache.value.size > 0) {
      // Cache is ready, proceed immediately
      getActivityDataFromCache(newValue);
    }
    // If cache is loading, the watch on isLoadingMakercartActivities will handle it
  } else {
    // Clear all data when no activity is selected
    resetMaterialsData();
    resetHardwareData();
    subjectIntegrationDisplay.value = "";
    subjectIntegration.value = "";
    makerApproach.value = "";
    grade.value = "";
    emit("subject-integration-updated", "");
    emit("subject-updated", "");
    emit("maker-approach-updated", "");
    emit("grade-updated", "");
  }
});

// Update functions for new fields
const updateMakercartActivity = (value) => {
  makercartActivity.value = value;
  emit("makercart-activity-updated", value);
  emit("airtable-id-updated", value);
};

const updateVisibility = (value) => {
  visibility.value = value;
  emit("visibility-updated", value);
};

const updateFocus = (value) => {
  focus.value = value;
  emit("focus-updated", value);
};

const updateGrade = (value) => {
  grade.value = value;
  emit("grade-updated", value);
};

// Sync selectedSchoolObjects with school (names) and selectedSchoolItems (display)
// Sync selectedSchoolObjects with school (objects) and selectedSchoolItems (display)
watch(
  selectedSchoolObjects,
  (newObjects) => {
    if (newObjects && newObjects.length > 0) {
      // Map to simpler objects for emitting
      school.value = newObjects.map((obj) => ({
        title: obj.name,
        country: obj.location,
      }));
      selectedSchoolItems.value = newObjects.map((obj) => obj.name); // Keep names for display tags
    } else {
      school.value = [];
      selectedSchoolItems.value = [];
    }
    emit("school-updated", school.value);
  },
  { deep: true }
);

// Get display text for school tags
const getSchoolDisplayText = (schoolText) => {
  return schoolText;
};

// Remove a school tag
const removeSchoolTag = (index) => {
  selectedSchoolObjects.value.splice(index, 1);
  // Watcher will handle updating school and selectedSchoolItems
};

// Update locale
const updateLocale = async (value) => {
  const newLocale = value || [];
  locale.value = newLocale;
  emit("locale-updated", locale.value);

  // Store currently selected activity to refresh it after locale change
  const currentlySelectedActivity = makercartActivity.value;

  // Refetch makercart activities with new locale
  await fetchMakercartActivities(newLocale);

  // If there was a selected activity, refresh its data from the updated cache
  if (
    currentlySelectedActivity &&
    makercartActivitiesCache.value.has(currentlySelectedActivity)
  ) {
    getActivityDataFromCache(currentlySelectedActivity);
  }
};

// Fetch locales
const fetchLocales = async () => {
  try {
    isLoadingLocales.value = true;
    const response = await stemStudioService.getLocales();
    const apiLocales = response?.data?.data?.locales || [];
    localeOptions.value = apiLocales.map((loc) => ({
      text: loc.code || loc.value,
      value: loc.value || loc.code,
    }));

    // No default value needed
    emit("locale-updated", locale.value);
  } catch (error) {
    console.error("Error fetching locales:", error);
    // Fallback options if API fails
    localeOptions.value = [
      { text: "US", value: "US" },
      { text: "SG", value: "SG" },
    ];
    emit("locale-updated", locale.value);
  } finally {
    isLoadingLocales.value = false;
  }
};

// Method to update from parent data
const updateFromParent = (
  newCoverImage,
  newTagSections,
  newPermalink,
  newFormData,
  editFormData,
  newCategorySections,
  newLocale
) => {
  console.log("RightSidebar: Updating from parent data", {
    newCoverImage,
    newTagSections,
    newPermalink,
    newFormData,
    editFormData,
    newCategorySections,
  });

  // Handle cover image
  if (newCoverImage) {
    coverImageUrl.value = newCoverImage;
  } else {
    coverImageUrl.value = null;
    // Make sure file input is reset
    if (fileInputRef.value) {
      fileInputRef.value.value = "";
    }
  }

  // Update permalink if provided
  if (newPermalink !== undefined) {
    permalink.value = newPermalink;
  }

  // Handle edit form data from API response (for EditFormPage)
  if (editFormData) {
    // Set airtable_id as makercart activity
    if (editFormData.airtable_id !== undefined) {
      makercartActivity.value = editFormData.airtable_id;
      emit("makercart-activity-updated", editFormData.airtable_id);
      emit("airtable-id-updated", editFormData.airtable_id);
    }

    // Set focus
    if (editFormData.focus !== undefined) {
      focus.value = editFormData.focus;
      emit("focus-updated", editFormData.focus);
    }

    // Set subject integration
    if (editFormData.subject !== undefined) {
      subjectIntegration.value = editFormData.subject;
      subjectIntegrationDisplay.value = editFormData.subject;
      emit("subject-integration-updated", editFormData.subject);
      emit("subject-updated", editFormData.subject);
    }

    // Set maker approach
    if (editFormData.maker_approach !== undefined) {
      makerApproach.value = editFormData.maker_approach;
      emit("maker-approach-updated", editFormData.maker_approach);
    }

    // Set hardware from editFormData
    if (editFormData.hardware_associated !== undefined) {
      if (!isHardwareEmpty(editFormData.hardware_associated)) {
        if (Array.isArray(editFormData.hardware_associated)) {
          hardwareList.value = [...editFormData.hardware_associated];
        } else if (typeof editFormData.hardware_associated === "string") {
          hardwareList.value = [editFormData.hardware_associated];
        }
        emit("hardware-updated", hardwareList.value);
      } else {
        resetHardwareData();
      }
    }

    // Handle material_provided array and populate materials
    if (
      editFormData.material_provided &&
      Array.isArray(editFormData.material_provided)
    ) {
      // Separate materials based on includedInMC
      const materialsProvided = editFormData.material_provided
        .filter((material) => material.includedInMC === true)
        .map((material) => material.name);

      const materialsNotProvided = editFormData.material_provided
        .filter((material) => material.includedInMC === false)
        .map((material) => material.name);

      // Update the display lists
      materialsProvidedList.value = materialsProvided;
      youllNeedToProvideList.value = materialsNotProvided;

      // Update the text fields
      materialsProvided.value = materialsProvided.join(", ");
      youllNeedToProvide.value = materialsNotProvided.join(", ");

      // Emit updates to parent
      emit("materials-provided-updated", materialsProvided.value);
      emit("youll-need-to-provide-updated", youllNeedToProvide.value);
    }

    // Set visibility from editFormData
    if (editFormData.public_visibility !== undefined) {
      visibility.value = editFormData.public_visibility
        ? "public_preview"
        : "restricted_view";
      emit("visibility-updated", visibility.value);
    } else if (visibility.value === "" && editFormData.status) {
      // Map status to visibility - you may need to adjust this mapping based on your business logic
      visibility.value =
        editFormData.status === "published"
          ? "public_preview"
          : "restricted_view";
      emit("visibility-updated", visibility.value);
    }

    // Set school from editFormData
    if (
      editFormData.school_objects !== undefined &&
      editFormData.school_objects &&
      Array.isArray(editFormData.school_objects)
    ) {
      // Handle new school_objects format
      const schools = editFormData.school_objects;

      school.value = schools;
      selectedSchoolItems.value = schools.map((s) => s.title);

      // Initialize objects for SchoolSelectionInput
      selectedSchoolObjects.value = schools.map((s, index) => ({
        id: `init-${index}`,
        name: s.title,
        location: s.country,
        isNew: false,
      }));

      emit("school-updated", school.value);
    } else if (
      editFormData.school_names !== undefined &&
      editFormData.school_names
    ) {
      let names = [];
      if (Array.isArray(editFormData.school_names)) {
        names = [...editFormData.school_names];
      } else if (typeof editFormData.school_names === "string") {
        names = editFormData.school_names.split(",").map((name) => name.trim());
      }

      school.value = names.map((name) => ({ title: name, country: "" }));
      selectedSchoolItems.value = names;

      // Initialize objects for SchoolSelectionInput
      selectedSchoolObjects.value = names.map((name, index) => ({
        id: `init-${index}`,
        name: name,
        location: "",
        isNew: false,
      }));

      emit("school-updated", school.value);
    } else if (
      editFormData.school_ids !== undefined &&
      editFormData.school_ids
    ) {
      // Fallback for older data that still uses IDs
      const schoolIds = editFormData.school_ids
        .split(",")
        .map((id) => parseInt(id.trim()));

      // If school options aren't loaded yet, wait for them
      if (schoolOptions.value.length > 0) {
        const names = schoolIds.map((schoolId) => {
          const schoolOption = schoolOptions.value.find(
            (option) => option.value === schoolId
          );
          return schoolOption ? schoolOption.text : schoolId.toString();
        });
        school.value = names;
        selectedSchoolItems.value = names;

        // Initialize objects for SchoolSelectionInput
        selectedSchoolObjects.value = names.map((name, index) => ({
          id: `init-pid-${index}`,
          name: name,
          location: "",
          isNew: false,
        }));

        emit("school-updated", school.value);
      } else {
        const unwatch = watch(schoolOptions, (newOptions) => {
          if (newOptions.length > 0) {
            const names = schoolIds.map((schoolId) => {
              const schoolOption = newOptions.find(
                (option) => option.value === schoolId
              );
              return schoolOption
                ? schoolOption.text || schoolOption.value
                : schoolId.toString();
            });
            school.value = names;
            selectedSchoolItems.value = names;

            // Initialize objects for SchoolSelectionInput
            selectedSchoolObjects.value = names.map((name, index) => ({
              id: `init-id-${index}`,
              name: name,
              location: "",
              isNew: false,
            }));

            emit("school-updated", school.value);
            unwatch();
          }
        });
      }
    }

    // Set grade directly from editFormData
    if (editFormData.grade !== undefined && editFormData.grade) {
      grade.value = editFormData.grade;
      emit("grade-updated", editFormData.grade);
    }

    // Set locale from editFormData if available
    if (editFormData.locale !== undefined && editFormData.locale) {
      let parsedLoc = editFormData.locale;
      if (typeof parsedLoc === "string") {
        parsedLoc = parsedLoc.split(",").map((item) => item.trim());
      }
      locale.value = parsedLoc;
      emit("locale-updated", locale.value);
    }

    // Handle grade field - populate the Grade section
    if (editFormData.grade !== undefined && editFormData.grade) {
      // Wait for categories to be loaded, then set the grade
      const setGrade = () => {
        const gradeSection = tagSections.find(
          (section) => section.title.toLowerCase() === "grade"
        );

        if (gradeSection) {
          // Format the grade value properly for the section
          const gradeValue = JSON.stringify({
            id: editFormData.grade,
            title: editFormData.grade,
          });

          // Set the grade selection
          gradeSection.selectedValue = gradeValue;
          gradeSection.selectedItems = [gradeValue];
          gradeSection.selectedValues = [gradeValue];

          // Emit grade update
          emit("grade-updated", editFormData.grade);

          // Trigger tag sections update
          emitUpdatedTagSections();
        }
      };

      // If categories are already loaded, set grade immediately
      if (tagSections.length > 0 && !isLoading.value) {
        setGrade();
      } else {
        // Otherwise, wait for categories to load
        const unwatch = watch(
          [isLoading, tagSections],
          ([loading, sections]) => {
            if (!loading && sections.length > 0) {
              setGrade();
              unwatch();
            }
          }
        );
      }
    }
  }

  // Update new form fields if provided (for FormPage compatibility)
  if (newFormData) {
    if (newFormData.makercartActivity !== undefined) {
      makercartActivity.value = newFormData.makercartActivity;
    }
    if (newFormData.materialsProvided !== undefined) {
      materialsProvided.value = newFormData.materialsProvided;
    }
    if (newFormData.youllNeedToProvide !== undefined) {
      youllNeedToProvide.value = newFormData.youllNeedToProvide;
    }
    if (newFormData.subjectIntegration !== undefined) {
      subjectIntegration.value = newFormData.subjectIntegration;
    }
    if (newFormData.makerApproach !== undefined) {
      makerApproach.value = newFormData.makerApproach;
    }
    if (newFormData.visibility !== undefined) {
      visibility.value = newFormData.visibility;
    }
    if (newFormData.focus !== undefined) {
      focus.value = newFormData.focus;
    }
    if (newFormData.school !== undefined) {
      school.value = newFormData.school;

      // Update selectedSchoolItems for display
      if (newFormData.school && newFormData.school.length > 0) {
        selectedSchoolItems.value = newFormData.school.map((schoolId) => {
          const schoolOption = schoolOptions.value.find(
            (option) => option.value === schoolId
          );
          return schoolOption ? schoolOption.text : schoolId.toString();
        });
      } else {
        selectedSchoolItems.value = [];
      }
    }
  }

  // Apply locale from the additional parameter if provided
  if (newLocale !== undefined && newLocale !== null && newLocale !== "") {
    locale.value = newLocale;
    emit("locale-updated", locale.value);
  }

  // Only process tag sections when they exist
  if (newTagSections && newTagSections.length > 0) {
    // Ensure all categories are loaded first
    if (tagSections.length === 0 || isLoading.value) {
      // If categories aren't loaded yet, store the data and process after loading
      const tempTagSections = [...newTagSections];
      // Use a watcher to apply the sections once loading is complete
      const unwatch = watch(isLoading, (newValue) => {
        if (!newValue && tagSections.length > 0) {
          // Now that categories are loaded, process the sections
          processTagSections(tempTagSections);
          // Remove the watcher
          unwatch();
        }
      });
    } else {
      // Categories are already loaded, process directly
      processTagSections(newTagSections);
    }
  }

  // Process category sections when they exist
  if (newCategorySections && newCategorySections.length > 0) {
    // Ensure all categories are loaded first
    if (categorySections.length === 0 || isLoadingCategories.value) {
      // If categories aren't loaded yet, store the data and process after loading
      const tempCategorySections = [...newCategorySections];
      // Use a watcher to apply the sections once loading is complete
      const unwatch = watch(isLoadingCategories, (newValue) => {
        if (!newValue && categorySections.length > 0) {
          // Now that categories are loaded, process the sections
          processCategorySections(tempCategorySections);
          // Remove the watcher
          unwatch();
        }
      });
    } else {
      // Categories are already loaded, process directly
      processCategorySections(newCategorySections);
    }
  }
};

// Helper function to process tag sections
const processTagSections = (newTagSections) => {
  // Map the incoming sections to the existing tagSections
  newTagSections.forEach((newSection) => {
    // Find the matching section in tagSections
    const existingSection = tagSections.find(
      (section) =>
        section.id === newSection.id || section.title === newSection.title
    );

    if (existingSection) {
      // Ensure the section options are loaded if needed
      if (
        !loadedOptions.value.has(existingSection.id) &&
        existingSection.id !== "standards"
      ) {
        fetchCategoryOptions(existingSection.id).then(() => {
          // After options are loaded, update the selections
          updateSectionSelections(existingSection, newSection.selected);
        });
      } else {
        // Options already loaded, update selections immediately
        updateSectionSelections(existingSection, newSection.selected);
      }
    }
  });
};

// Helper function to update section selections
const updateSectionSelections = (section, selectedItems) => {
  if (!selectedItems || !selectedItems.length) return;

  // Check if this is a Grade section (single selection)
  const isGradeSection = section.title.toLowerCase() === "grade";

  if (isGradeSection) {
    // For Grade section, only take the first item and set it as single selection
    const firstItem = selectedItems[0];

    let formattedItem;
    if (typeof firstItem === "string" && firstItem.startsWith("{")) {
      formattedItem = firstItem;
    } else {
      try {
        if (typeof firstItem === "object" && firstItem.id) {
          formattedItem = JSON.stringify(firstItem);
        } else {
          formattedItem = JSON.stringify({
            id: firstItem,
            title: firstItem,
          });
        }
      } catch (e) {
        console.error("Error formatting selected item:", e);
        formattedItem = firstItem;
      }
    }

    // Update Grade section with single selection
    section.selectedValue = formattedItem;
    section.selectedItems = [formattedItem];
    section.selectedValues = [formattedItem];
  } else {
    // Process multiple items for non-Grade sections
    const formattedItems = selectedItems.map((item) => {
      // If the item is already a string that looks like JSON, keep it as is
      if (typeof item === "string" && item.startsWith("{")) {
        return item;
      }

      // Otherwise, try to convert it to our expected format
      try {
        // If it's already a parsed object with id and title
        if (typeof item === "object" && item.id) {
          return JSON.stringify(item);
        }

        // If it's a string but not JSON formatted, create a new object
        return JSON.stringify({
          id: item,
          title: item,
        });
      } catch (e) {
        console.error("Error formatting selected item:", e);
        return item; // fallback to original value
      }
    });

    // Update the section with the formatted selections
    section.selectedItems = formattedItems;
    section.selectedValues = [...formattedItems];

    // Force UI update
    setTimeout(() => {
      section.selectedValues = [...formattedItems];
    }, 0);
  }
};

// Helper function to process category sections
const processCategorySections = (newCategorySections) => {
  // Map the incoming sections to the existing categorySections
  newCategorySections.forEach((newSection) => {
    // Find the matching section in categorySections
    const existingSection = categorySections.find(
      (section) =>
        section.id === newSection.id || section.title === newSection.title
    );

    if (existingSection) {
      // Ensure the section options are loaded if needed
      if (!loadedCategoryOptions.value.has(existingSection.id)) {
        fetchGeneralCategoryOptions(existingSection.id).then(() => {
          // After options are loaded, update the selections
          updateCategorySectionSelections(existingSection, newSection.selected);
        });
      } else {
        // Options already loaded, update selections immediately
        updateCategorySectionSelections(existingSection, newSection.selected);
      }
    }
  });
};

// Helper function to update category section selections
const updateCategorySectionSelections = (section, selectedItems) => {
  if (!selectedItems || !selectedItems.length) {
    // Ensure arrays are initialized even when no items selected
    section.selectedItems = [];
    section.selectedValues = [];
    return;
  }

  // Process multiple items for category sections
  const formattedItems = selectedItems.map((item) => {
    // If the item is already a string that looks like JSON, keep it as is
    if (typeof item === "string" && item.startsWith("{")) {
      return item;
    }

    // Otherwise, try to convert it to our expected format
    try {
      // If it's already a parsed object with id and title
      if (typeof item === "object" && item.id) {
        return JSON.stringify(item);
      }

      // If it's a string but not JSON formatted, create a new object
      return JSON.stringify({
        id: item,
        title: item,
      });
    } catch (e) {
      console.error("Error formatting selected item:", e);
      return item; // fallback to original value
    }
  });

  // Update the section with the formatted selections
  section.selectedItems = [...formattedItems];
  section.selectedValues = [...formattedItems];

  // Force UI update
  setTimeout(() => {
    section.selectedValues = [...formattedItems];
  }, 0);
};

// Expose the method to parent
defineExpose({
  updateFromParent,
});

// Emit updated tag sections
const emitUpdatedTagSections = () => {
  // Format the tag sections for emit
  const formattedTagSections = tagSections.map((section) => ({
    id: section.id,
    title: section.title,
    selected: section.selectedItems || [],
  }));

  emit("tag-sections-updated", formattedTagSections);

  // Also emit grade specifically if it's updated
  const gradeSection = tagSections.find(
    (section) => section.title.toLowerCase() === "grade"
  );
  if (gradeSection && gradeSection.selectedItems.length > 0) {
    try {
      const gradeValue = JSON.parse(gradeSection.selectedItems[0]);
      emit("grade-updated", gradeValue.title || gradeValue.id);
    } catch (e) {
      emit("grade-updated", gradeSection.selectedItems[0]);
    }
  } else {
    emit("grade-updated", "");
  }
};

// Watch for changes in tagSections and emit updates
watch(
  tagSections,
  () => {
    emitUpdatedTagSections();
  },
  { deep: true }
);

// Watch for changes in categorySections and emit updates
watch(
  categorySections,
  () => {
    emitUpdatedCategorySections();
  },
  { deep: true }
);

// Watch categorySections to ensure proper array initialization
watch(
  categorySections,
  (newSections) => {
    newSections.forEach((section) => {
      if (!Array.isArray(section.selectedItems)) {
        section.selectedItems = [];
      }
      if (!Array.isArray(section.selectedValues)) {
        section.selectedValues = [];
      }
    });
  },
  { immediate: true, deep: true }
);

// Watch for visibility changes to handle school section
watch(visibility, (newVisibility) => {
  if (newVisibility === "public_preview") {
    // Clear school selections when switching to public preview
    school.value = [];
    selectedSchoolItems.value = [];
    emit("school-updated", []);
  }
});

// Watch for when makercart activities finish loading
watch(isLoadingMakercartActivities, (isLoading) => {
  if (
    !isLoading &&
    makercartActivity.value &&
    makercartActivitiesCache.value.size > 0
  ) {
    // Activities finished loading and we have a selected activity, process it
    getActivityDataFromCache(makercartActivity.value);
  }
});

// Emit initial cover image if available
if (coverImageUrl.value) {
  emit("cover-image-updated", coverImageUrl.value);
}

// Fetch categories when component is mounted
onMounted(() => {
  fetchCategories(); // Fetch general categories (excluding school)
  fetchSchoolCategories(); // Fetch school-specific categories
  fetchMakercartActivities(locale.value); // Pass current locale to fetch
  fetchLocales();
});
</script>

<style>
/* Unscoped: teleported dropdowns render outside this component's scope */
.select-dropdown.variant-form {
  height: auto;
  width: auto;
  min-width: 360px;
}
</style>

<style scoped>

.right-sidebar-title {
  width: 100%;
  margin-left: 5px;
  font-weight: 500;
  font-size: 1em;
  color: var(--primary-blue);
  letter-spacing: 0.01em;
}

.right-sidebar-item {
  border-bottom: 1px solid #d8dae6;
  padding-top: 1rem;
  padding-bottom: 1rem;
  transition: background-color 0.2s ease;
}

/* Loading and Empty States */
.loading-container,
.empty-state {
  padding: 20px;
  text-align: center;
  color: #666;
  font-size: 0.9em;
  border-bottom: 1px solid #d8dae6;
}

.loading-container p,
.empty-state p {
  margin: 0;
}

/* Standards main accordion styling */
.standards-main-accordion {
  margin-bottom: 8px;
}

.standards-main-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--primary-blue);
  font-weight: 600;
  font-size: 1.05em;
}

.standards-icon {
  color: var(--primary-blue);
  font-size: 1.2em;
}

.standards-accordion-content {
  padding: 0;
  margin: 0;
}

/* Nested standards categories styling */
.standards-category {
  border: none;
  border-radius: 0;
  margin: 0;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.standards-category:last-child {
  border-bottom: none;
}

.standards-category .right-sidebar-title {
  color: var(--primary-blue);
  margin-left: 15px;
}

.empty-tags-message {
  font-size: 0.85em;
  color: #666;
  font-style: italic;
  padding: 8px 0;
}

.options-loading-indicator {
  font-size: 0.85em;
  color: #666;
  font-style: italic;
  margin-top: 5px;
  text-align: center;
  padding: 5px;
  background-color: rgba(0, 61, 106, 0.03);
  border-radius: 4px;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
}

.content-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Cover Image Styles */
.image-preview-container {
  margin-bottom: 12px;
  max-width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  background-color: #f8f9fa;
  position: relative;
  display: flex;
  flex-direction: column;
}

.empty-image-message {
  padding: 15px;
  background-color: rgba(0, 61, 106, 0.03);
  border: 1px dashed rgba(0, 61, 106, 0.2);
  border-radius: 6px;
  color: #666;
  font-size: 0.9em;
  text-align: center;
  margin-bottom: 12px;
}

.remove-image-button {
  margin-top: 8px;
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.85em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease;
  align-self: center;
  margin-bottom: 8px;
}

.remove-image-button:hover {
  background-color: rgba(220, 53, 69, 0.2);
}

.image-preview {
  max-width: 100%;
  max-height: 160px;
  border-radius: 6px;
  display: block;
  object-fit: cover;
  width: 100%;
}

.upload-button {
  display: inline-block;
  padding: 10px 18px;
  background-color: var(--primary-blue);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  text-align: center;
  font-size: 0.9em;
  font-weight: 500;
  transition: all 0.2s ease;
  width: fit-content;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.upload-button:hover {
  background-color: #00497e;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
}

.upload-button:active {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Select Container Styles */
.select-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

@media (min-width: 768px) {
  .select-container {
    flex-direction: row;
    align-items: center;
  }

  .select-container .input-wrap {
    flex-grow: 1;
  }

  .select-container .add-button {
    min-width: 60px;
    width: auto;
    margin-top: 0;
  }
}

/* Tag Styles */
.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
  min-height: 0; /* Remove minimum height to fix spacing */
}

.tags-container:not(:empty) {
  margin-bottom: 12px; /* Add margin only if there are tags */
  background-color: rgba(0, 61, 106, 0.03);
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(0, 61, 106, 0.07);
}

.tag {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.07) 0%,
    rgba(0, 61, 106, 0.12) 100%
  );
  color: var(--primary-blue);
  padding: 6px 12px;
  border-radius: 18px;
  font-size: 0.85em;
  font-weight: 500;
  border: 1px solid rgba(0, 61, 106, 0.1);
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.tag:hover {
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.1) 0%,
    rgba(0, 61, 106, 0.15) 100%
  );
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.delete-tag-button {
  background: rgba(255, 255, 255, 0.6);
  border: none;
  color: var(--primary-blue);
  margin-left: 6px;
  cursor: pointer;
  font-size: 0.6em;
  line-height: 1;
  padding: 1px;
  opacity: 0.8;
  transition: all 0.2s ease;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-tag-button:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.9);
  color: #e63946;
}

.add-button {
  padding: 10px 16px;
  background: linear-gradient(135deg, #00925e 0%, #007c50 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9em;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.add-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #00a06a 0%, #008a59 100%);
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
}

.add-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.add-button:disabled {
  background: linear-gradient(135deg, #e2e2e2 0%, #d8d8d8 100%);
  color: #999;
  cursor: not-allowed;
  box-shadow: none;
}

/* Add subtle animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.tag {
  animation: fadeIn 0.3s ease;
}

.image-preview {
  animation: fadeIn 0.5s ease;
}

/* Permalink styles */
.permalink-container {
  width: 100%;
}

.permalink-helper {
  font-size: 0.85em;
  color: #666;
  margin-top: 5px;
  padding: 5px;
  background-color: rgba(0, 61, 106, 0.03);
  border-radius: 4px;
}

.permalink-input {
  width: 100%;
}

/* Loading States */
.loading-activities,
.loading-materials {
  text-align: center;
  padding: 20px;
  color: #666;
  font-style: italic;
}

.loading-activities p,
.loading-materials p {
  margin: 0;
  animation: pulse 1.5s infinite;
}

.materials-display {
  width: 100%;
}

.materials-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.material-item {
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.05) 0%,
    rgba(0, 61, 106, 0.08) 100%
  );
  border: 1px solid rgba(0, 61, 106, 0.1);
  border-radius: 18px;
  padding: 8px 14px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  flex: 0 0 auto;
  white-space: nowrap;
}

.material-item:hover {
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.08) 0%,
    rgba(0, 61, 106, 0.12) 100%
  );
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.material-name {
  font-size: 0.85em;
  font-weight: 500;
  color: var(--primary-blue);
  display: inline-block;
  line-height: 1.2;
}

.empty-materials-message {
  font-size: 0.9em;
  color: #666;
  font-style: italic;
  padding: 20px;
  text-align: center;
  background-color: rgba(0, 61, 106, 0.03);
  border: 1px dashed rgba(0, 61, 106, 0.2);
  border-radius: 8px;
  line-height: 1.5;
}

/* Subject Integration Display Styles */
.subject-integration-display {
  width: 100%;
}

.subject-integration-item {
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.05) 0%,
    rgba(0, 61, 106, 0.08) 100%
  );
  border: 1px solid rgba(0, 61, 106, 0.1);
  border-radius: 18px;
  padding: 12px 18px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  display: inline-block;
}

.subject-integration-item:hover {
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.08) 0%,
    rgba(0, 61, 106, 0.12) 100%
  );
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.subject-integration-name {
  font-size: 0.9em;
  font-weight: 600;
  color: var(--primary-blue);
  display: inline-block;
  line-height: 1.3;
}

/* Hardware Display Styles */
.hardware-display {
  width: 100%;
}

.hardware-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.hardware-item {
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.05) 0%,
    rgba(0, 61, 106, 0.08) 100%
  );
  border: 1px solid rgba(0, 61, 106, 0.1);
  border-radius: 18px;
  padding: 8px 14px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  flex: 0 0 auto;
  white-space: nowrap;
}

.hardware-item:hover {
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.08) 0%,
    rgba(0, 61, 106, 0.12) 100%
  );
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.hardware-name {
  font-size: 0.85em;
  font-weight: 500;
  color: var(--primary-blue);
  display: inline-block;
  line-height: 1.2;
}

/* Maker Approach Display Styles */
.maker-approach-display {
  width: 100%;
}

.maker-approach-item {
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.05) 0%,
    rgba(0, 61, 106, 0.08) 100%
  );
  border: 1px solid rgba(0, 61, 106, 0.1);
  border-radius: 12px;
  padding: 15px 18px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  display: block;
}

.maker-approach-item:hover {
  background: linear-gradient(
    135deg,
    rgba(0, 61, 106, 0.08) 0%,
    rgba(0, 61, 106, 0.12) 100%
  );
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.maker-approach-name {
  font-size: 0.9em;
  font-weight: 500;
  color: var(--primary-blue);
  display: block;
  line-height: 1.4;
}

/* Disabled School Section Styles */
.disabled-section {
  opacity: 0.6;
  pointer-events: none;
}

.disabled-section .right-sidebar-item {
  background-color: rgba(128, 128, 128, 0.1);
}

.disabled-title {
  color: #999 !important;
  font-style: italic;
}

.disabled-indicator {
  font-size: 0.75em;
  color: #666;
  font-weight: normal;
  margin-left: 8px;
}

.disabled-school-message {
  background-color: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 10px;
}

.disabled-school-message p {
  margin: 0;
  color: #856404;
  font-size: 0.9em;
  line-height: 1.4;
  font-style: italic;
}

/* Loading and Empty States for Categories */
.loading-container,
.empty-state {
  padding: 20px;
  text-align: center;
  color: #666;
  font-size: 0.9em;
  border-bottom: 1px solid #d8dae6;
}

.loading-container p,
.empty-state p {
  margin: 0;
}
</style>
